import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '@/components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Package, TrendingUp, Truck, DollarSign, Plus, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import DashboardDateTimeWidget from '@/components/layout/DashboardDateTimeWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'status-warning',
  assigned: 'status-neutral',
  in_transit: 'status-active',
  delivered: 'status-active',
  cancelled: 'status-error',
};

const DashboardHome = () => {
  const [analytics, setAnalytics] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newShipment, setNewShipment] = useState({
    origin: '',
    destination: '',
    cargo_type: '',
    weight: '',
    pickup_date: '',
    delivery_date: '',
    special_instructions: '',
    value: ''
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [analyticsRes, shipmentsRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/shipments`),
        axios.get(`${API}/analytics/monthly-volume`)
      ]);
      setAnalytics(analyticsRes.data);
      setShipments(shipmentsRes.data);
      setMonthlyData(monthlyRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateShipment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/shipments`, {
        ...newShipment,
        weight: parseFloat(newShipment.weight),
        value: newShipment.value ? parseFloat(newShipment.value) : null
      });
      toast.success('Shipment created successfully');
      setDialogOpen(false);
      setNewShipment({
        origin: '',
        destination: '',
        cargo_type: '',
        weight: '',
        pickup_date: '',
        delivery_date: '',
        special_instructions: '',
        value: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shipment');
    }
  };

  const stats = [
    { label: 'Total Shipments', value: analytics?.total_shipments || 0, icon: Package, color: 'text-primary' },
    { label: 'In Transit', value: analytics?.in_transit || 0, icon: Truck, color: 'text-emerald-400' },
    { label: 'Pending', value: analytics?.pending_shipments || 0, icon: TrendingUp, color: 'text-amber-400' },
    { label: 'Total Value', value: `$${(analytics?.total_value || 0).toLocaleString()}`, icon: DollarSign, color: 'text-primary' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your shipments and analytics</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchData} disabled={isLoading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 shadow-glow" data-testid="create-shipment-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Shipment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Create Shipment</DialogTitle>
                <DialogDescription>Fill in the details for your new shipment</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateShipment} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="origin">Origin</Label>
                    <Input
                      id="origin"
                      value={newShipment.origin}
                      onChange={(e) => setNewShipment({ ...newShipment, origin: e.target.value })}
                      placeholder="City, State"
                      required
                      data-testid="shipment-origin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="destination">Destination</Label>
                    <Input
                      id="destination"
                      value={newShipment.destination}
                      onChange={(e) => setNewShipment({ ...newShipment, destination: e.target.value })}
                      placeholder="City, State"
                      required
                      data-testid="shipment-destination-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cargo_type">Cargo Type</Label>
                    <Input
                      id="cargo_type"
                      value={newShipment.cargo_type}
                      onChange={(e) => setNewShipment({ ...newShipment, cargo_type: e.target.value })}
                      placeholder="e.g., Electronics"
                      required
                      data-testid="shipment-cargo-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weight">Weight (lbs)</Label>
                    <Input
                      id="weight"
                      type="number"
                      value={newShipment.weight}
                      onChange={(e) => setNewShipment({ ...newShipment, weight: e.target.value })}
                      placeholder="0"
                      required
                      data-testid="shipment-weight-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pickup_date">Pickup Date</Label>
                    <Input
                      id="pickup_date"
                      type="date"
                      value={newShipment.pickup_date}
                      onChange={(e) => setNewShipment({ ...newShipment, pickup_date: e.target.value })}
                      required
                      data-testid="shipment-pickup-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_date">Delivery Date</Label>
                    <Input
                      id="delivery_date"
                      type="date"
                      value={newShipment.delivery_date}
                      onChange={(e) => setNewShipment({ ...newShipment, delivery_date: e.target.value })}
                      required
                      data-testid="shipment-delivery-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="value">Shipment Value ($)</Label>
                  <Input
                    id="value"
                    type="number"
                    value={newShipment.value}
                    onChange={(e) => setNewShipment({ ...newShipment, value: e.target.value })}
                    placeholder="Optional"
                    data-testid="shipment-value-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instructions">Special Instructions</Label>
                  <Textarea
                    id="instructions"
                    value={newShipment.special_instructions}
                    onChange={(e) => setNewShipment({ ...newShipment, special_instructions: e.target.value })}
                    placeholder="Any special handling requirements..."
                    data-testid="shipment-instructions-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" data-testid="submit-shipment-btn">
                  Create Shipment
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={stat.label} className="card-hover animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider">{stat.label}</p>
                  <p className="text-3xl font-heading font-bold mt-2">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg bg-muted ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading">Shipment Volume</CardTitle>
            <CardDescription>Monthly shipment count</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="shipments" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading">Revenue Trend</CardTitle>
            <CardDescription>Monthly shipment value</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Value']}
                  />
                  <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: 'hsl(var(--primary))' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Shipments Table */}
      <Card className="card-hover">
        <CardHeader>
          <CardTitle className="font-heading">Recent Shipments</CardTitle>
          <CardDescription>Your latest shipment activities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tracking #</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No shipments yet. Create your first shipment!
                    </TableCell>
                  </TableRow>
                ) : (
                  shipments.slice(0, 5).map((shipment) => (
                    <TableRow key={shipment.id} data-testid={`shipment-row-${shipment.id}`}>
                      <TableCell className="font-mono text-sm">{shipment.tracking_number}</TableCell>
                      <TableCell>{shipment.origin}</TableCell>
                      <TableCell>{shipment.destination}</TableCell>
                      <TableCell>{shipment.cargo_type}</TableCell>
                      <TableCell>
                        <span className={statusColors[shipment.status]}>
                          {shipment.status.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell>${(shipment.value || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ShipmentsPage = () => {
  const [shipments, setShipments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const response = await axios.get(`${API}/shipments`);
        setShipments(response.data);
      } catch (error) {
        toast.error('Failed to fetch shipments');
      } finally {
        setIsLoading(false);
      }
    };
    fetchShipments();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-heading font-bold tracking-tight">All Shipments</h1>
      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tracking #</TableHead>
                <TableHead>Origin</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Weight</TableHead>
                <TableHead>Pickup</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shipments.map((shipment) => (
                <TableRow key={shipment.id}>
                  <TableCell className="font-mono text-sm">{shipment.tracking_number}</TableCell>
                  <TableCell>{shipment.origin}</TableCell>
                  <TableCell>{shipment.destination}</TableCell>
                  <TableCell>{shipment.cargo_type}</TableCell>
                  <TableCell>{shipment.weight} lbs</TableCell>
                  <TableCell>{shipment.pickup_date}</TableCell>
                  <TableCell>{shipment.delivery_date}</TableCell>
                  <TableCell>
                    <span className={statusColors[shipment.status]}>
                      {shipment.status.replace('_', ' ')}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const AnalyticsPage = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [statusData, setStatusData] = useState([]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const [monthlyRes, statusRes] = await Promise.all([
          axios.get(`${API}/analytics/monthly-volume`),
          axios.get(`${API}/analytics/shipments-by-status`)
        ]);
        setMonthlyData(monthlyRes.data);
        setStatusData(statusRes.data);
      } catch (error) {
        toast.error('Failed to fetch analytics');
      }
    };
    fetchAnalytics();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-heading font-bold tracking-tight">Analytics</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Monthly Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                  <Bar dataKey="shipments" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Shipments by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {statusData.map((item) => (
                <div key={item.status} className="flex items-center justify-between">
                  <span className={statusColors[item.status]}>{item.status.replace('_', ' ')}</span>
                  <span className="font-bold text-2xl">{item.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const DistributorDashboard = () => {
  return (
    <div className="min-h-screen bg-background">
      <Sidebar role="distributor" />
      <main className="lg:ml-64 p-6 lg:p-10" data-testid="distributor-dashboard">
        <div className="flex justify-end mb-4">
          <DashboardDateTimeWidget />
        </div>
        <Routes>
          <Route index element={<DashboardHome />} />
          <Route path="shipments" element={<ShipmentsPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
        </Routes>
      </main>
    </div>
  );
};

export default DistributorDashboard;
